/**
 * KOAI 메인 레이아웃
 * 접근 레벨별 사이드바 메뉴 표시
 */

import { useState, useMemo, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Description as DescriptionIcon } from '@mui/icons-material';
import { promptClinicAPI, deciCraftAPI, usersAPI } from '@/services/api';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  ChevronRight,
  ExpandMore,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { getMenusBySection, getLevelNumber } from '@/config/menuConfig';

const DRAWER_WIDTH = 240;

// 레벨별 표시 정보
const levelInfo = {
  1: { label: '무료', color: 'default' },
  2: { label: '스탠다드', color: 'primary' },
  3: { label: '팀 매니저', color: 'warning' },
  4: { label: '기업 관리자', color: 'error' },
  5: { label: '플랫폼 관리자', color: 'secondary' },
};

export default function MainLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [savedResults, setSavedResults] = useState({ promptclinic: [], decicraft: [] });
  const [teamStatusDialog, setTeamStatusDialog] = useState({ open: false, data: null });

  // 사용자 레벨에 따라 표시할 메뉴 필터링 (섹션별)
  const mainMenus = useMemo(() => {
    return getMenusBySection(user || { accessLevel: 'Level1' }, 'main');
  }, [user]);

  const adminMenus = useMemo(() => {
    return getMenusBySection(user || { accessLevel: 'Level1' }, 'admin');
  }, [user]);

  // 저장된 결과 가져오기
  useEffect(() => {
    if (!user) return;

    const fetchSavedResults = async () => {
      try {
        const [promptClinicRes, deciCraftRes] = await Promise.all([
          promptClinicAPI.getSaved({ limit: 20 }).catch(() => ({ data: { success: false } })),
          deciCraftAPI.getSaved({ limit: 20 }).catch(() => ({ data: { success: false } })),
        ]);

        setSavedResults({
          promptclinic: promptClinicRes.data?.success ? promptClinicRes.data.data.items || [] : [],
          decicraft: deciCraftRes.data?.success ? deciCraftRes.data.data.items || [] : [],
        });
      } catch (error) {
        console.error('Failed to fetch saved results:', error);
      }
    };

    fetchSavedResults();
  }, [user]);

  // Level 3 팀 매니저: 로그인 시 팀원 상태 확인 대화상자
  useEffect(() => {
    if (!user || getLevelNumber(user.accessLevel) !== 3) return;
    const myTeamIds = user.teamIds || [];
    if (myTeamIds.length === 0) return;

    const fetchTeamStatus = async () => {
      try {
        const res = await usersAPI.getAll({ limit: 500 });
        const users = res.data?.data?.users || [];
        const teamMembers = users.filter(u => {
          const theirs = u.teamIds || [];
          return theirs.some(id => myTeamIds.includes(id));
        });
        const inactiveCount = teamMembers.filter(u => u.status === 'inactive').length;
        setTeamStatusDialog({
          open: true,
          data: {
            total: teamMembers.length,
            inactive: inactiveCount,
            active: teamMembers.length - inactiveCount
          }
        });
      } catch (err) {
        console.error('Failed to fetch team status:', err);
      }
    };

    fetchTeamStatus();
  }, [user?.id, user?.accessLevel, user?.teamIds?.length]);

  const docsMenus = useMemo(() => {
    return getMenusBySection(user || { accessLevel: 'Level1' }, 'docs');
  }, [user]);

  const extensionMenus = useMemo(() => {
    const menus = getMenusBySection(user || { accessLevel: 'Level1' }, 'extensions');
    
    // 저장된 결과를 메뉴에 동적으로 추가
    return menus.map(menu => {
      if (menu.expandable && menu.children) {
        const updatedChildren = menu.children.map(child => {
          const extensionType = child.key === 'promptclinic' ? 'promptclinic' : child.key === 'decicraft' ? 'decicraft' : null;
          if (!extensionType) return child;
          
          const savedItems = savedResults[extensionType] || [];
          const savedChildren = savedItems.map(item => ({
            key: `saved-${extensionType}-${item.id}`,
            path: `${child.path}?saved=${item.id}`,
            icon: DescriptionIcon,
            minLevel: 1,
            labelKey: item.title,
            isSaved: true,
          }));
          
          return {
            ...child,
            children: savedChildren.length > 0 ? savedChildren : undefined,
          };
        });
        
        return {
          ...menu,
          children: updatedChildren,
        };
      }
      return menu;
    });
  }, [user, savedResults]);

  // 현재 레벨 정보 (표시용)
  const currentLevel = useMemo(() => {
    const levelNum = getLevelNumber(user);
    return levelInfo[levelNum] || levelInfo[1];
  }, [user]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/');
  };

  const handleToggleExpand = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  // 저장된 결과 삭제
  const handleDeleteSavedResult = async (savedKey, extensionType) => {
    const id = savedKey.split('-').pop();
    const extension = extensionType === 'promptclinic' ? 'promptclinic' : 'decicraft';
    
    if (!window.confirm('이 저장된 결과를 삭제하시겠습니까?')) return;
    
    try {
      if (extension === 'promptclinic') {
        await promptClinicAPI.deleteSaved(id);
      } else {
        await deciCraftAPI.deleteSaved(id);
      }
      
      // 로컬 상태 업데이트
      setSavedResults(prev => ({
        ...prev,
        [extension]: prev[extension].filter(item => item.id !== parseInt(id))
      }));
      
      // 현재 페이지가 삭제된 결과를 보고 있다면 홈으로 이동
      if (location.search.includes(`saved=${id}`)) {
        navigate(`/extensions/${extension}`);
      }
    } catch (error) {
      console.error('Failed to delete saved result:', error);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // 확장 가능한 메뉴 렌더링 헬퍼 함수
  const renderExpandableMenuItem = (item) => {
    const Icon = item.icon;
    const isExpanded = expandedMenus[item.key] || false;
    const hasActiveChild = item.children?.some(child => 
      location.pathname === child.path || location.pathname.startsWith(child.path + '/')
    );
    const isActive = location.pathname === item.path || 
                    location.pathname.startsWith(item.path + '/') ||
                    hasActiveChild;

    return (
      <>
        <ListItem key={item.key} disablePadding>
          <ListItemButton
            selected={isActive}
            onClick={() => handleToggleExpand(item.key)}
          >
            <ListItemIcon>
              <Icon color={isActive ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary={t(item.labelKey)} />
            {isExpanded ? <ExpandMore /> : <ChevronRight />}
          </ListItemButton>
        </ListItem>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children?.map((child) => {
              const ChildIcon = child.icon;
              const isChildActive = location.pathname === child.path || 
                                  location.pathname.startsWith(child.path + '/');
              const hasSavedChildren = child.children && child.children.length > 0;
              const isChildExpanded = expandedMenus[child.key] || false;
              
              return (
                <Box key={child.key}>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={isChildActive && !hasSavedChildren}
                      onClick={() => {
                        if (hasSavedChildren) {
                          handleToggleExpand(child.key);
                        } else {
                          navigate(child.path);
                          setMobileOpen(false);
                        }
                      }}
                      sx={{ pl: 4 }}
                    >
                      <ListItemIcon>
                        <ChildIcon color={isChildActive ? 'primary' : 'inherit'} />
                      </ListItemIcon>
                      <ListItemText primary={t(child.labelKey)} />
                      {hasSavedChildren && (isChildExpanded ? <ExpandMore /> : <ChevronRight />)}
                    </ListItemButton>
                  </ListItem>
                  {hasSavedChildren && (
                    <Collapse in={isChildExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {child.children.map((savedChild) => {
                          const SavedIcon = savedChild.icon;
                          const isSavedActive = location.pathname === savedChild.path.split('?')[0] && 
                                               location.search.includes(`saved=${savedChild.key.split('-').pop()}`);
                          
                          return (
                            <ListItem key={savedChild.key} disablePadding>
                              <ListItemButton
                                selected={isSavedActive}
                                onClick={() => {
                                  navigate(savedChild.path);
                                  setMobileOpen(false);
                                }}
                                sx={{ pl: 6, pr: 1 }}
                              >
                                <ListItemIcon>
                                  <SavedIcon color={isSavedActive ? 'primary' : 'inherit'} fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={savedChild.labelKey} 
                                  primaryTypographyProps={{ 
                                    variant: 'body2',
                                    sx: { 
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: '120px'
                                    }
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSavedResult(savedChild.key, child.key);
                                  }}
                                  sx={{ 
                                    ml: 1, 
                                    opacity: 0.7,
                                    '&:hover': { opacity: 1, color: 'error.main' }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
        <Box
          component="img"
          src="/koai-mascot.png"
          alt="KOAI CI"
          sx={{
            height: 32,
            width: 32,
            borderRadius: '50%',
            objectFit: 'cover',
            mr: 1,
          }}
        />
        <Typography variant="h5" noWrap component="div" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
          KOAI
        </Typography>
      </Toolbar>
      <Divider />
      
      {/* 사용자 레벨 표시 */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <Chip 
          label={currentLevel.label}
          color={currentLevel.color}
          size="small"
          variant="outlined"
        />
      </Box>
      <Divider />

      {/* 메인 메뉴 */}
      <List>
        {mainMenus.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
                          location.pathname.startsWith(item.path + '/');
          
          return (
            <ListItem key={item.key} disablePadding>
              <ListItemButton
                selected={isActive}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
              >
                <ListItemIcon>
                  <Icon color={isActive ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText primary={t(item.labelKey)} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* 관리 메뉴 (Level3+) */}
      {adminMenus.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ px: 2, py: 1, display: 'block' }}
          >
            관리
          </Typography>
          <List>
            {adminMenus.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || 
                              location.pathname.startsWith(item.path + '/');
              
              return (
                <ListItem key={item.key} disablePadding>
                  <ListItemButton
                    selected={isActive}
                    onClick={() => {
                      navigate(item.path);
                      setMobileOpen(false);
                    }}
                  >
                    <ListItemIcon>
                      <Icon color={isActive ? 'primary' : 'inherit'} />
                    </ListItemIcon>
                    <ListItemText primary={t(item.labelKey)} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </>
      )}

      {/* 확장 메뉴 */}
      {extensionMenus.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <List>
            {extensionMenus.map((item) => {
              if (item.expandable) {
                return renderExpandableMenuItem(item);
              } else {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                                location.pathname.startsWith(item.path + '/');
                
                return (
                  <ListItem key={item.key} disablePadding>
                    <ListItemButton
                      selected={isActive}
                      onClick={() => {
                        navigate(item.path);
                        setMobileOpen(false);
                      }}
                    >
                      <ListItemIcon>
                        <Icon color={isActive ? 'primary' : 'inherit'} />
                      </ListItemIcon>
                      <ListItemText primary={t(item.labelKey)} />
                    </ListItemButton>
                  </ListItem>
                );
              }
            })}
          </List>
        </>
      )}

      {/* Documentation — 좌 하단 고정 */}
      {docsMenus.length > 0 && (
        <Box sx={{ mt: 'auto' }}>
          <Divider sx={{ my: 1 }} />
          <List>
            {docsMenus.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path ||
                              location.pathname.startsWith(item.path + '/');

              return (
                <ListItem key={item.key} disablePadding>
                  <ListItemButton
                    selected={isActive}
                    onClick={() => {
                      navigate(item.path);
                      setMobileOpen(false);
                    }}
                  >
                    <ListItemIcon>
                      <Icon color={isActive ? 'primary' : 'inherit'} />
                    </ListItemIcon>
                    <ListItemText primary={t(item.labelKey)} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { sm: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled>
              <Typography variant="body2">{user?.email}</Typography>
            </MenuItem>
            <MenuItem disabled>
              <Chip 
                label={currentLevel.label}
                color={currentLevel.color}
                size="small"
              />
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t('nav.logout')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              display: 'flex',
              flexDirection: 'column',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: '64px',
          overflow: 'auto',
        }}
      >
        <Outlet />
      </Box>

      {/* Level 3 팀 매니저: 로그인 시 팀원 상태 확인 대화상자 */}
      {getLevelNumber(user?.accessLevel) === 3 && teamStatusDialog.open && teamStatusDialog.data && (
        <Dialog open={teamStatusDialog.open} onClose={() => setTeamStatusDialog(prev => ({ ...prev, open: false }))} maxWidth="xs" fullWidth>
          <DialogTitle>팀원 상태 확인</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" paragraph>
              소속 팀원 현황입니다.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body1">총 팀원: <strong>{teamStatusDialog.data.total}명</strong></Typography>
              <Typography variant="body1">활성: <strong>{teamStatusDialog.data.active}명</strong></Typography>
              <Typography variant="body1" color={teamStatusDialog.data.inactive > 0 ? 'warning.main' : 'text.secondary'}>
                퇴사/비활성: <strong>{teamStatusDialog.data.inactive}명</strong>
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTeamStatusDialog(prev => ({ ...prev, open: false }))} variant="contained">
              확인
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
